import React from 'react';
import { Link } from 'react-router-dom';

interface Pillar {
  icon: React.ReactNode;
  title: string;
  body: string;
  link?: { label: string; to: string };
}

const pillars: Pillar[] = [
  {
    title: "We don't touch the drugs.",
    body:
      "LiberaCure is a routing layer, not a pharmacy. An Indian generic supplier with fifteen-plus years of physical operation packs and ships your order directly to you. We never warehouse the product — which structurally means we cannot run off with it.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.25} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
      </svg>
    ),
  },
  {
    title: 'Reship twice. Refund third.',
    body:
      "If your package doesn't arrive, we reship. If that doesn't arrive, we reship again. Both free, no forms. If the third attempt still doesn't land, we refund in crypto to the address you paid from. It's posted, not buried.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.25} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
    ),
  },
  {
    title: "You'd be right to be skeptical.",
    body:
      "We're a new domain that only takes crypto. Of course you're hesitant. We wrote a whole page about it — every objection, every sketchy-looking detail, answered plainly. Read it before you buy. Then decide.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.25} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>
    ),
    link: { label: 'Read why, honestly', to: '/honestly' },
  },
];

const WhyLiberaCure: React.FC = () => (
  <section className="py-24 lg:py-32 bg-cream-50 border-y border-warm-200">
    <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
      <div className="max-w-2xl mb-14 lg:mb-20">
        <p className="text-caption text-gold-500 font-medium tracking-[0.2em] uppercase mb-4">
          Why LiberaCure
        </p>
        <h2 className="font-display text-display-sm lg:text-display-md text-forest-900 tracking-tight mb-6">
          Honest about the structure. Strict about the product.
        </h2>
        <p className="text-body-lg text-warm-600 tracking-wide leading-relaxed">
          We can&rsquo;t compete with twenty-year-old pharmacies on reputation —
          we don&rsquo;t have twenty years. What we can do is tell you exactly
          how this works, post every promise in the open, and make the small
          first order easy enough to test us cheaply.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
        {pillars.map((p) => (
          <div key={p.title} className="flex flex-col">
            <div className="w-12 h-12 rounded-full bg-white border border-warm-200 flex items-center justify-center text-gold-500 mb-6">
              {p.icon}
            </div>
            <h3 className="font-display text-heading-lg text-forest-900 tracking-tight mb-3 italic">
              {p.title}
            </h3>
            <p className="text-body text-warm-600 leading-relaxed">{p.body}</p>
            {p.link && (
              <Link
                to={p.link.to}
                className="mt-5 inline-flex items-center gap-1.5 text-caption font-semibold text-gold-700 hover:text-gold-600 uppercase tracking-[0.15em] transition-colors group/link self-start"
              >
                {p.link.label}
                <svg
                  className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default WhyLiberaCure;
